/**
 * Shared helpers of the server-side list search / filters (fiches 22986 /
 * 22987 / 22988 / 22989):
 *
 * - {@link buildFilterParams}: the request params of a list from its search
 *   text and filter values, each trimmed — a blank value (empty search,
 *   « Tous … » / « Toutes … » option) is **not sent** at all;
 * - `WS2_EA_SEL_*` option lists (`{ VAL, LIB }` rows, API order):
 *   {@link mapSelOptions} / {@link fetchSelOptions}, used by the status
 *   selects of « Mes contrats » (`WS2_EA_SEL_STATUT_CONTRAT`), « Mes primes »
 *   (`WS2_EA_SEL_STATUT_PRIME`) and « Mes sinistres »
 *   (`WS2_EA_SEL_STATUT_SINISTRE`).
 */

import { formatText, type VeosScalar } from '../utils/format';
import { fetchErpRows } from './erpProxy';

/**
 * Request params of a list search / filter: every value trimmed, blank ones
 * (`null` / `undefined` / `''` / spaces) dropped so they are not sent.
 * Keys are the VEOS parameter names, cased as in the fiche.
 */
export function buildFilterParams(
  values: Readonly<Record<string, VeosScalar>>,
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    const text = formatText(value);
    if (text !== '') params[key] = text;
  }
  return params;
}

/** One raw `WS2_EA_SEL_*` row. */
export interface SelOptionRow {
  VAL: string | null;
  LIB: string | null;
}

/** A select option: `VAL` → the request value, `LIB` → the label. */
export interface SelOption {
  value: string;
  label: string;
}

/**
 * Map `WS2_EA_SEL_*` rows to options, in API order: rows without a value are
 * dropped, a blank label falls back to the value. Labels are kept as VEOS
 * returns them (e.g. `IMPAYEE` / `PAYEE`).
 */
export function mapSelOptions(rows: readonly SelOptionRow[]): SelOption[] {
  const options: SelOption[] = [];
  for (const row of rows) {
    const value = formatText(row.VAL);
    if (value === '') continue;
    const label = formatText(row.LIB);
    options.push({ value, label: label === '' ? value : label });
  }
  return options;
}

/** GET a `WS2_EA_SEL_*` endpoint (no caller param) and map its options. */
export async function fetchSelOptions(
  registrySlug: string | null | undefined,
  endpointSlug: string,
): Promise<SelOption[]> {
  const rows = await fetchErpRows<SelOptionRow>(registrySlug, endpointSlug);
  return mapSelOptions(rows);
}
